/**
 * The sticky buy bar.
 *
 * Two jobs: show itself once the real call to action has scrolled out of view,
 * and stay in step with whatever the visitor selects above.
 *
 * It mirrors the primary buy button rather than recomputing anything. The buy
 * panel has already worked out the variant, the price, the enquiry link and
 * whether the button should be disabled, so copying that is both less code and
 * incapable of disagreeing with what is on screen.
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.watched = document.querySelector(this.dataset.watch);
    if (!this.watched) return;

    this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting), {
      threshold: 0,
    });
    this.observer.observe(this.watched);

    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('variant:change', this.onVariantChange);
    document.documentElement.classList.remove('sticky-bar-open');
  }

  toggle(show) {
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', String(!show));

    // Publishes its state and its measured height so anything else pinned to
    // the bottom edge — the floating contact button — can move out of the way
    // by exactly the right amount, and only while the bar is actually there.
    //
    // A class rather than `:has()` in CSS: `:has()` needs Safari 15.4, and an
    // iPhone a version older than that would silently get the overlap back.
    const root = document.documentElement;
    root.classList.toggle('sticky-bar-open', show);
    if (show) root.style.setProperty('--sticky-bar-height', `${this.offsetHeight}px`);
  }


  onVariantChange(event) {
    const { variant } = event.detail;
    if (!variant) return;

    const price = this.querySelector('[data-sticky-price]');
    if (price) price.textContent = variant.price;

    const meta = this.querySelector('[data-sticky-meta]');
    if (meta) meta.textContent = variant.title;

    this.mirror(variant);
  }

  /** Copies the primary CTA's state onto this bar's own copy of it. */
  mirror(variant) {
    const panel = document.querySelector('product-info');
    if (!panel) return;

    const sourceLink = panel.querySelector('[data-inquire-button]');
    const ownLink = this.querySelector('[data-inquire-button]');
    if (sourceLink && ownLink) ownLink.href = sourceLink.href;

    const ownId = this.querySelector('[data-variant-id]');
    if (ownId) ownId.value = variant.id;

    const sourceButton = panel.querySelector('[data-buy-button]');
    const ownButton = this.querySelector('[data-buy-button]');
    if (sourceButton && ownButton) {
      ownButton.disabled = sourceButton.disabled;
      const sourceLabel = sourceButton.querySelector('[data-buy-label]');
      const ownLabel = ownButton.querySelector('[data-buy-label]');
      if (sourceLabel && ownLabel) ownLabel.textContent = sourceLabel.textContent;
    }
  }
}

customElements.define('sticky-buy-bar', StickyBuyBar);
